import { useCallback, useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-enterprise";
import { Dialog, IconButton, styled, Tooltip } from "@mui/material";
import { Button } from "react-bootstrap";
import {
  deleteDocuments,
  downloadFile,
  getDocumentsData,
  GetMultipleDocuments,
} from "../../api/documentsApi/documentsApi";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NavigationBar from "../NavigationBar/NavigationBarComponent";
import "../../config/colors.css";
import { DocumentsRequestData } from "../../types/DocumentsModel";
import { useLoader } from "../../context/LoaderContext";
import { useLocation } from "react-router-dom";
import { useSnackbar } from "../Common/SnackbarContext";
import CustomLoadingOverlay from "../CustomLoadingOverlay/CustomLoadingOverlay ";
import { appSettings } from "../../config/appSetting";
import { urlConfig } from "../../config/urlConfig";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import InfoDrawer from "../SimpleSearch/InfoDrawer";
import { DialogProps, DocumentView } from "../../types/SimpleSearchModel";
import ConfirmationDialog from "../SimpleSearch/ConfirmationDialog";
import FileUploads from "../Alert/FileUploads";
import { FormatDate } from "../Common/CommonMethods";
import isPagePermission from "../../utils/common";
import LocalStorageUtil from "../../utils/localStorage";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const Documents: React.FC = () => {
  const pageSizeSelector = appSettings.agGridPageSizeSelector;
  const defaulPageSize = appSettings.agGridDefaulPageSize;
  const { showSnackbar } = useSnackbar();
  const [columnDefs, setColumnDefs] = useState<ColDef[]>([]);
  const [rowData, setRowData] = useState<any>();
  const gridRef = useRef<AgGridReact>(null);
  const { setLoading } = useLoader();
  const location = useLocation();
  const folderData = location.state?.payload.folderId;
  const selectedFolderData = location.state?.payload;
  const [uploadFilesOpen, setUploadFilesOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const selectedFolderPath = location?.state?.path;
  const toggleDrawer = (open: boolean) => () => {
    setDrawerOpen(open);
  };

  const handelUploadFiles = () => {
    setUploadFilesOpen(true);
  };

  const closeUploadFiles = () => {
    setUploadFilesOpen(false);
    getData();
  };

  //Page load data
  useEffect(() => {
    if (folderData) {
      getData();
    }
  }, [folderData]);

  //File download action
  const fileDownload = async (params: any) => {
    const objDownloadFile = {
      documentStreamID: params.documentStreamID,
      contextName: LocalStorageUtil.getItem("contextName")?.toString(),
    };
    try {
      setLoading(true);
      const response = await downloadFile(objDownloadFile);
      if (response.status === 200) {
        const data = response.data;
        const contentType = response.headers["content-type"];
        const linkElement = document.createElement("a");
        const blob = new Blob([data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        linkElement.href = url;
        linkElement.setAttribute("download", params.downloadFileName);

        const clickEvent = new MouseEvent("click", {
          view: window,
          bubbles: true,
          cancelable: false,
        });
        linkElement.dispatchEvent(clickEvent);
      }

      if (response) {
        showSnackbar("Documents downloaded successfully");
      } else {
        showSnackbar("Unable to download file !", true);
      }
    } catch (error) {
      console.log("Something went wrong:" + error);
    } finally {
      setLoading(false);
    }
  };

  //Show the document in a popup
  const popupCenter = (url: string, title: string, w: number, h: number) => {
    const dualScreenLeft = window.screenLeft ?? window.screenX ?? 0;
    const dualScreenTop = window.screenTop ?? window.screenY ?? 0;

    const width =
      window.innerWidth ||
      document.documentElement.clientWidth ||
      window.screen.width;

    const height =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      window.screen.height;

    const left = width / 3 - w / 3 + dualScreenLeft;
    const top = height / 3 - h / 3 + dualScreenTop;
    const newWindow = window.open(
      url,
      title,
      `readonly=true, scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`
    );
    if (newWindow) {
      newWindow.focus();
    }
  };

  //View Action the document
  const viewDocument = (rowData: any) => {
    const id = rowData.documentStreamID;
    const encodedId = btoa(id);
    const fileName = rowData.downloadFileName.replace(/\.[^/.]+$/, "");
    const url = !urlConfig.isDeployed
      ? `/documentviewer/${encodedId}?FileName=${fileName}`
      : `/docdistrov2/documentviewer/${encodedId}?FileName=${fileName}`;
    popupCenter(url, "Document Viewer", 1100, 550);
  };

  //Rename the columns
  const renameColumns = (val: any) => {
    // NOSONAR
    if (val !== undefined) {
      if (val.toLowerCase() === "projectreference") {
        return "Project ID";
      } else if (
        val.toLowerCase() === "projectdocumentid" ||
        val.toLowerCase() === "project_id"
      ) {
        return "Document ID";
      } else if (val.toLowerCase() === "displayname") {
        return "Display Name";
      } else if (val.toLowerCase() === "level 1") {
        return "Rendition";
      } else if (
        val.toLowerCase() === "uploadby" ||
        val.toLowerCase() === "username"
      ) {
        return "Uploaded By";
      } else if (val.toLowerCase() === "uploadedon") {
        return "Uploaded On";
      } else if (val == "actioN CODE") {
        // First character is capitalized before checking here, will always fail
        return "Action Code";
      } else if (val.toLowerCase() === "ngfamily_id") {
        return "Family_Id";
      } else if (val.toLowerCase() === "statement_date") {
        return "Date";
      } else if (val.toLowerCase() === "sub_dob") {
        return "DateOfBirth";
      } else if (val.toLowerCase() === "sub_name") {
        return "Name";
      } else if (val.toLowerCase() === "lastmodifieddate") {
        return "Last Modified Date";
      } else if (val.toLowerCase() === "filename") {
        return "File Name";
      } else {
        return val.replace(/&amp;/, "&");
      }
    }
  };

  //Restricted Columns
  const RestrictedColumns = [
    "rowNum",
    "filetype",
    "documentID",
    "documentStreamID",
    "documentStatus",
    "queueType",
    "recordCounts",
    "latestDocumentDesc",
    "rendition_Intern",
    "project_Reference",
    "agentLastName",
    "renameRemarks",
    "resolutionStatus",
    "parentDocumentStreamID",
    "DocumentStatus",
    "thumbnail",
    "fileSize",
    "Field",
    "toPreflightZipName",
    "fundFolderId",
    "statusName",
    "fileType",
    "daoflag",
    "downloadFileName",
    "accessPermission",
  ];

  // Helper to get dynamic columns
  const getDynamicColumns = (
    data: any[],
    RestrictedColumns: string[],
    renameColumns: (val: any) => string
  ) => {
    return Object.keys(data[0])
      .filter((key) => !RestrictedColumns.includes(key))
      .map((key) => {
        if (
          key.toLocaleLowerCase().includes("date") &&
          key.toLocaleLowerCase() !== "document date"
        ) {
          return {
            headerName: renameColumns(
              key.charAt(0).toUpperCase() + key.slice(1)
            ),
            field: key,
            sortable: true,
            filter: true,
            enableRowGroup: true,
            valueFormatter: (params: any) => FormatDate(params.value),
          };
        } else {
          return {
            headerName: renameColumns(
              key.charAt(0).toUpperCase() + key.slice(1)
            ),
            field: key,
            sortable: true,
            filter: true,
            enableRowGroup: true,
          };
        }
      });
  };

  // Helper to get combined column definitions
  const getCombinedColumnDefs = (
    dynamicColumns: any[],
    actionCellRenderer: any
  ) => [
    {
      width: 10,
      sortable: false,
      filter: false,
      hide: false,
      colId: "colCheckbox",
      enableRowGroup: false,
      resizable: false,
    },
    {
      headerName: "#",
      width: 70,
      colId: "colRowNum",
      field: "rowNum",
      sortable: false,
      filter: false,
      resizable: false,
      enableRowGroup: false,
    },
    ...dynamicColumns,
    {
      field: "Actions",
      headerName: "Actions",
      sortable: false,
      enableRowGroup: false,
      pinned: "right" as const,
      lockPinned: true,
      suppressSizeToFit: true,
      width: 100,
      minwidth: 100,
      cellClass: "actions-button-cell",
      cellRenderer: actionCellRenderer,
      filter: false,
      resizable: false,
      colId: "colAction",
    },
  ];

  // GetData function
  const getData = async () => {
    try {
      setLoading(true);
      const payload: DocumentsRequestData = {
        AccountId: Number(LocalStorageUtil.getItem("accountId")),
        UserId: Number(LocalStorageUtil.getItem("userId")),
        Filename: "",
        FolderId: folderData,
        contextName: (LocalStorageUtil.getItem("contextName") ?? "").toString(),
        FolderRights: (
          LocalStorageUtil.getItem("folderRights") ?? ""
        ).toString(),
        IsPdfSplitEnabled: "False",
        IsSuperAdmin: Number(LocalStorageUtil.getItem("isSuperAdmin")),
        currentPage: 0,
        noOfRecords: 0,
        sortOrder: 1,
      };
      const response = await getDocumentsData(payload);
      if (response.listDocViewModel) {
        setRowData(response.listDocViewModel);

        // Dynamically get column headers from the data
        const dynamicColumns = getDynamicColumns(
          response.listDocViewModel,
          RestrictedColumns,
          renameColumns
        );
        const combinedColumnDefs = getCombinedColumnDefs(
          dynamicColumns,
          actionCellRenderer
        );
        setColumnDefs(combinedColumnDefs);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  //Action cell renderer
  const actionCellRenderer = (params: any) => {
    if (params.data === undefined) {
      return "";
    } else {
      return (
        <div>
          {isPagePermission("Allow Download", "Documents", 252) && (
            <Tooltip title="Click here to download the documents">
              <IconButton
                data-testid="download-button"
                className="icon-button"
                style={{ padding: "2px" }}
                onClick={() => fileDownload(params.data)}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
          )}
          {([35, 1025].includes(
            Number(LocalStorageUtil.getItem("businessUnitId"))
          )
            ? selectedRows?.every((row) =>
                ["1", "2", "3", "4", "5", "6", "7"].includes(
                  row.accessPermission
                )
              )
            : isPagePermission("Allow DocumentPreView", "Documents", 257)) && (
            <Tooltip title="Click to view">
              <IconButton
                className="icon-button"
                style={{ padding: "2px" }}
                onClick={() => viewDocument(params.data)}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      );
    }
  };

  //#region Quick Filter
  //Searching
  const onFilterTextBoxChanged = useCallback(() => {
    const filterText = (
      document.getElementById("filter-text-box") as HTMLInputElement
    ).value;

    gridRef.current?.api.setGridOption("quickFilterText", filterText);
  }, []);
  //#endregion

  // #region Top Action Buttons
  const [dialogProps, setDialogProps] = useState<DialogProps>({
    dialogTitle: "",
    contentText: "",
    confirmText: "",
    closeText: "",
    onConfirm: null,
  });

  const onSelectionChanged = () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    setSelectedRows(selectedRows || []);
  };

  //Info Document - Action buttons events
  const infoDocument = (rowData: any) => {
    setSelectedDocument(rowData);
    toggleDrawer(true)();
  };

  const clearSelectedRows = () => {
    gridRef.current!.api.deselectAll();
  };

  const handleDownloadMultipleFiles = async () => {
    try {
      if (gridRef.current) {
        const selectedRows = gridRef.current.api.getSelectedRows();
        let multiFileDownload = null;
        if (selectedRows.length > 1) {
          let documentStreamID: number[] = [];

          selectedRows.forEach((item: any) => {
            documentStreamID.push(item.documentStreamID);
          });

          if (documentStreamID.length > 1) {
            multiFileDownload = {
              Filename: documentStreamID.toString(),
              contextName: LocalStorageUtil.getItem("contextName")?.toString(),
            };
          }
        }

        setLoading(true);
        const response = await GetMultipleDocuments(multiFileDownload);
        setLoading(false);

        if (response.status === 200) {
          const data = response.data;
          const contentType = response.headers["content-type"];
          const fileName = "documents.zip";

          const linkElement = document.createElement("a");

          const blob = new Blob([data], { type: contentType });
          const url = window.URL.createObjectURL(blob);
          linkElement.href = url;
          linkElement.setAttribute("download", fileName);

          const clickEvent = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: false,
          });
          linkElement.dispatchEvent(clickEvent);
        }

        if (response) {
          showSnackbar("Documents downloaded successfully");
          clearSelectedRows();
        } else {
          showSnackbar("Unable to download file !", true);
        }
        setDialogOpen(false);
      }
    } catch (error) {
      showSnackbar("Something went wrong ! Please try again.", true);
    } finally {
      setDialogOpen(false);
      setLoading(false);
    }
  };

  // Helper to check for duplicate file names
  const hasDuplicateFileNames = (selectedRows: any[]) => {
    const fileNameMap: Record<string, number> = {};
    for (const item of selectedRows) {
      const name = item.downloadFileName?.toLowerCase();
      if (fileNameMap[name]) {
        return true;
      }
      fileNameMap[name] = 1;
    }
    return false;
  };

  // Helper to get documentStreamIDs
  const getDocumentStreamIDs = (selectedRows: any[]) =>
    selectedRows.map((item: any) => item.documentStreamID);

  // Multiple file download
  const multipleFileDownload = () => {
    if (!gridRef.current) return;

    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length === 0) {
      showSnackbar("Please select the files to Zip and Download", true);
      return;
    }

    if (selectedRows.length === 1) {
      showSnackbar(
        "Please select more than one file to Zip and Download",
        true
      );
      return;
    }

    showSnackbar("Download started ...");
    const documentStreamID = getDocumentStreamIDs(selectedRows);

    if (documentStreamID.length <= 1) return;

    if (hasDuplicateFileNames(selectedRows)) {
      setDialogProps({
        dialogTitle: "Notification",
        contentText:
          "Please note that your Selection Criteria contains duplicate files.Downloaded Duplicate files will be renamed.",
        confirmText: "OK",
        closeText: "Cancel",
        onConfirm: handleDownloadMultipleFiles,
      });
      setDialogOpen(true);
    } else {
      handleDownloadMultipleFiles();
    }
  };

  //Print document
  const printDocument = (selectedRows: any) => {
    const rowsToPrint = selectedRows.length > 0 ? selectedRows : rowData;
    const printContent = `
      <table border="1" style="width: 100%; border-collapse: collapse;">
        <thead>
          <tr>
            ${columnDefs
              .filter(
                (col) => col.field !== "Actions" && col.colId !== "colCheckbox"
              )
              .map((col) => `<th>${col.headerName}</th>`)
              .join("")}
          </tr>
        </thead>
        <tbody>
          ${rowsToPrint
            .map(
              (row: any) => `
            <tr>
              ${columnDefs
                .filter(
                  (col) =>
                    col.field !== "Actions" && col.colId !== "colCheckbox"
                )
                .map((col) =>
                  col.field ? `<td>${row[col.field]}</td>` : `<td></td>`
                )
                .join("")}
            </tr>
          `
            )
            .join("")}
        </tbody>
      </table>
    `;

    // Create a hidden iframe for printing
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "absolute";
    printFrame.style.top = "-10000px";
    document.body.appendChild(printFrame);

    const frameDoc =
      printFrame.contentWindow?.document || printFrame.contentDocument;
    if (frameDoc) {
      frameDoc.open();
      frameDoc.write(`
        <html>
          <head>
            <title>Print Documents</title>
            <style>
              body { font-family: Arial, sans-serif; }
              table { width: 100%; border-collapse: collapse; }
              th, td { padding: 8px; text-align: left; border: 1px solid #ddd; font-size: 8px; }
              th { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      frameDoc.close();
      // Trigger the print dialog
      printFrame.contentWindow?.focus();
      printFrame.contentWindow?.print();
      // Remove the iframe after printing
      document.body.removeChild(printFrame);
    }
  };
  const handleDeleteDocument = async (selectedRows: any) => {
    debugger;
    try {
      if (selectedRows.length > 0) {
        const documentStreamID: number[] = [];
        selectedRows.forEach((item: any) => {
          documentStreamID.push(item.documentStreamID);
        });

        let params: DocumentView[] = [];

        if (documentStreamID.length > 0) {
          selectedRows.forEach((item: any) => {
            const objDeleteDocument: any = {
              accountId: Number(LocalStorageUtil.getItem("accountId")),
              documentStreamId: item.documentStreamID,
              userId: Number(LocalStorageUtil.getItem("userId")),
              contextname: LocalStorageUtil.getItem("contextName")?.toString(),
            };
            params.push(objDeleteDocument);
          });
        }
        setLoading(true);
        const response = await deleteDocuments(params);
        setLoading(false);

        if (response === 200) {
          showSnackbar("Documents deleted successfully");
          clearSelectedRows();
          getData();
        } else {
          showSnackbar("Unable to delete file !", true);
        }
      }
    } catch (error) {
      showSnackbar("Something went wrong ! Please try again.", true);
    } finally {
      setDialogOpen(false);
      setLoading(false);
    }
  };

  //Delete document
  const deleteDocument = (selectedRows: any) => {
    if (selectedRows.length > 0) {
      setDialogProps({
        dialogTitle: "Delete Document",
        contentText:
          "Are you sure you want to delete the selected document(s)?",
        confirmText: "Yes",
        closeText: "No",
        onConfirm: () => handleDeleteDocument(selectedRows),
      });
      setDialogOpen(true);
    } else {
      showSnackbar("Please select the document to delete", true);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };
  // #endregion
  //Render the component
  return (
    <div>
      <NavigationBar title={selectedFolderPath} text="" />
      <div style={{ marginLeft: "2em", marginTop: ".1em" }}>
        <div
          className="col-md-10"
          style={{
            width: "100%",
            marginTop: ".5em",
            marginBottom: ".5em",
          }}
        >
          <input
            type="text"
            id="filter-text-box"
            placeholder="Quick Filter..."
            onChange={onFilterTextBoxChanged}
            style={{
              width: "25em",
              height: "2.5em",
              fontSize: "11px",
              paddingLeft: ".5em",
              marginBottom: ".1em",
            }}
          />

          <Tooltip title="Click to print the document">
            <IconButton
              className="icon-button"
              style={{
                padding: "0px",
                color: "var(--aggrid-text-color)",
                float: "right",
                marginRight: "1.5em",
              }}
              onClick={() => printDocument(selectedRows)}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          {selectedRows?.length > 0 &&
            ([35, 1025].includes(
              Number(LocalStorageUtil.getItem("businessUnitId"))
            )
              ? ["4", "5", "6", "7"].includes(selectedRows[0]?.accessPermission)
              : isPagePermission("Allow Delete", "Documents", 254)) && (
              <Tooltip title="Click to delete">
                <IconButton
                  data-testid="delete-button"
                  className="icon-button"
                  style={{
                    padding: "2px",
                    color: "var(--aggrid-text-color)",
                    float: "right",
                    marginRight: ".1em",
                  }}
                  onClick={() => deleteDocument(selectedRows)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          {selectedRows.length > 1 && (
            <>
              {isPagePermission("Allow Download", "Documents", 252) && (
                <Tooltip title="Click here to download the documents">
                  <IconButton
                    className="icon-button"
                    style={{
                      padding: "2px",
                      float: "right",
                      marginRight: ".1em",
                    }}
                    onClick={multipleFileDownload}
                  >
                    <FileDownloadIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {selectedRows.length === 1 && (
            <>
              {isPagePermission(
                "Allow Metadata Information",
                "Documents",
                255
              ) && (
                <Tooltip title="Click to view the file information and history">
                  <IconButton
                    className="icon-button"
                    style={{
                      padding: "2px",
                      color: "var(--aggrid-text-color)",
                      float: "right",
                      marginRight: ".1em",
                    }}
                    onClick={() => infoDocument(selectedRows)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
          {selectedFolderData.isUploadFiles &&
          isPagePermission("Allow Custom Upload", "Documents", 256) ? (
            <Button
              variant="contained"
              size="sm"
              className="document-button"
              onClick={handelUploadFiles}
              style={{
                float: "right",
              }}
            >
              Upload
            </Button>
          ) : null}
        </div>
        <div
          className="col-md-2"
          style={{
            marginTop: ".5em",
            marginBottom: ".5em",
          }}
        >
          {" "}
        </div>
        <div className="queue-table-view clearfix">
          <div
            className="ag-theme-quartz-dark"
            style={{
              height: "71vh",
              marginTop: ".1vh",
              width: "98%",
            }}
          >
            <AgGridReact
              columnDefs={columnDefs}
              rowData={rowData}
              pagination={true}
              rowGroupPanelShow="always"
              pivotPanelShow="always"
              gridOptions={{
                rowSelection: {
                  mode: "multiRow",
                  checkboxes: true,
                  headerCheckbox: true,
                  enableClickSelection: false,
                },
              }}
              ref={gridRef}
              headerHeight={30}
              paginationPageSize={defaulPageSize}
              paginationPageSizeSelector={pageSizeSelector}
              loading={!rowData}
              loadingOverlayComponent={CustomLoadingOverlay}
              overlayNoRowsTemplate={
                '<span class="ag-overlay-loading-center">No rows to show</span>'
              }
              onSelectionChanged={onSelectionChanged}
            />
          </div>
        </div>
        {/* Info Drawer */}
        <div>
          <InfoDrawer
            isOpen={drawerOpen}
            onClose={toggleDrawer(false)}
            title="Document Info"
            tabLabels={["Information", "Version History"]}
            selectedDocument={selectedDocument}
            screenType="Documents"
          />
        </div>
        <ConfirmationDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onConfirm={dialogProps.onConfirm}
          contentText={dialogProps.contentText}
          closeText={dialogProps.closeText}
          confirmText={dialogProps.confirmText}
          dialogTitle={dialogProps.dialogTitle}
        />
        {uploadFilesOpen && (
          <FileUploads
            isOpen={uploadFilesOpen}
            onClose={closeUploadFiles}
            title="Upload Files"
            selectedData={selectedFolderData}
            isOtherOption={true}
            screenType="Document"
          />
        )}
      </div>
    </div>
  );
};

export default Documents;
